import React from 'react'
import { useTranslation } from 'react-i18next'
import { LabelSemiBoldMedium } from 'components/base-ui/FontsV2'
import ExcludeProductCard, { type ProductType } from 'components/buffet/ProductCard'
import { type Vendor } from 'components/ProductCardV2'
import { BuffetPackageProducts } from 'hooks/buffet/useCategory'
import { MembershipProgramPublicType } from 'types/delivery'
import {
  ProductContent,
  CategoryName,
  IncludedProductWrapper,
  ProductListWrapper,
  ExcludedProductSection,
  ProductCard,
} from './styled'

interface BuffetProductListProps {
  categoryName?: string
  includedProducts: BuffetPackageProducts
  aLaCarteProducts: BuffetPackageProducts
  excludedProducts: ProductType[]
  onClickProduct: (id: number) => void
  onAddProductToCart: (id: number) => void
  isShowOnlyExcludedProducts: boolean
  vendor: Vendor
  membershipProgram: MembershipProgramPublicType
}

const BuffetProductList = ({
  categoryName,
  includedProducts,
  aLaCarteProducts,
  excludedProducts,
  onClickProduct,
  onAddProductToCart,
  isShowOnlyExcludedProducts,
  vendor,
  membershipProgram,
}: BuffetProductListProps) => {
  const { t, i18n } = useTranslation()

  return (
    <ProductContent id="product-list-section">
      {categoryName && <CategoryName>{categoryName}</CategoryName>}

      {includedProducts.length > 0 && (
        <IncludedProductWrapper>
          <LabelSemiBoldMedium>{t('buffet.productListPage.includedProducts')}</LabelSemiBoldMedium>
          <ProductListWrapper>
            {includedProducts.map((product, index) => (
              <ProductCard
                key={`included-product-${index}`}
                overflow
                addToCart
                vendor={vendor}
                lang={i18n.language}
                data={product}
                onAddToCart={(data) => onAddProductToCart(data.id)}
                onClickProduct={(product) => onClickProduct(product.id)}
                addToCartIconName="plus-2"
                membershipProgram={membershipProgram}
                nameCustomClass="product-name"
                addToCartIconClass="add-to-cart-icon"
                isOutline
                hideZeroPrice
              />
            ))}
          </ProductListWrapper>
        </IncludedProductWrapper>
      )}

      {aLaCarteProducts.length > 0 && (
        <IncludedProductWrapper>
          <LabelSemiBoldMedium>{t('buffet.productListPage.addonProducts')}</LabelSemiBoldMedium>
          <ProductListWrapper>
            {aLaCarteProducts.map((product, index) => (
              <ProductCard
                key={`a-la-carte-product-${index}`}
                overflow
                addToCart
                vendor={vendor}
                lang={i18n.language}
                data={product}
                onAddToCart={(data) => onAddProductToCart(data.id)}
                onClickProduct={(product) => onClickProduct(product.id)}
                addToCartIconName="plus-2"
                membershipProgram={membershipProgram}
                nameCustomClass="product-name"
                addToCartIconClass="add-to-cart-icon"
                isOutline
              />
            ))}
          </ProductListWrapper>
        </IncludedProductWrapper>
      )}

      {excludedProducts.length > 0 && (
        <ExcludedProductSection isShowOnlyExcludedProducts={isShowOnlyExcludedProducts}>
          <LabelSemiBoldMedium>{t('buffet.productListPage.excludedProducts')}</LabelSemiBoldMedium>
          <ProductListWrapper>
            {excludedProducts.map((product, index) => (
              <ExcludeProductCard key={`excluded-product-${index}`} product={product} onClick={onClickProduct} />
            ))}
          </ProductListWrapper>
        </ExcludedProductSection>
      )}
    </ProductContent>
  )
}

export default BuffetProductList
